import React, { useState, useCallback } from 'react';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
} from '@mui/icons-material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${
  typeof window !== 'undefined' ? window.location.origin : ''
}/pdf.worker.min.mjs`;

type ReadOnlyPdfViewerProps = {
  file: string;
  onLoadSuccess?: () => void;
  onLoadError?: (message: string) => void;
};

/**
 * Renders a PDF with pdf.js (no browser chrome). Used for read-only library docs
 * so Chrome's native viewer download/print controls are not shown.
 */
const ReadOnlyPdfViewer: React.FC<ReadOnlyPdfViewerProps> = ({
  file,
  onLoadSuccess,
  onLoadError,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [loading, setLoading] = useState(true);

  const handleDocumentLoad = useCallback(
    ({ numPages: next }: { numPages: number }) => {
      setNumPages(next);
      setLoading(false);
      onLoadSuccess?.();
    },
    [onLoadSuccess]
  );

  const handleDocumentError = useCallback(
    (err: Error) => {
      setLoading(false);
      onLoadError?.(err?.message || 'Failed to load PDF');
    },
    [onLoadError]
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'grey.200',
      }}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onDragStart={(e) => {
        e.preventDefault();
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.75,
          bgcolor: 'grey.900',
          color: 'common.white',
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ flex: 1 }}>
          {numPages > 0 ? `${numPages} page${numPages === 1 ? '' : 's'}` : ' '}
        </Typography>
        <IconButton
          size="small"
          aria-label="Zoom out"
          onClick={() =>
            setScale((s) => Math.max(0.5, Math.round((s - 0.1) * 10) / 10))
          }
          sx={{ color: 'common.white' }}
        >
          <ZoomOutIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'center' }}>
          {Math.round(scale * 100)}%
        </Typography>
        <IconButton
          size="small"
          aria-label="Zoom in"
          onClick={() =>
            setScale((s) => Math.min(2.5, Math.round((s + 0.1) * 10) / 10))
          }
          sx={{ color: 'common.white' }}
        >
          <ZoomInIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 2,
          gap: 2,
          userSelect: 'none',
        }}
      >
        {loading && (
          <Box sx={{ py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}
        <Document
          file={file}
          onLoadSuccess={handleDocumentLoad}
          onLoadError={handleDocumentError}
          loading=""
          error={
            <Typography color="error" sx={{ p: 2 }}>
              Failed to load document.
            </Typography>
          }
        >
          {Array.from({ length: numPages }, (_, i) => (
            <Box
              key={`page_${i + 1}`}
              sx={{
                boxShadow: 2,
                bgcolor: 'common.white',
              }}
            >
              <Page
                pageNumber={i + 1}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Box>
          ))}
        </Document>
      </Box>
    </Box>
  );
};

export default ReadOnlyPdfViewer;
